import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";

/**
 * Hand-drawn signature capture.
 *
 * Exposes `toDataURL()` / `clear()` / `isEmpty()` through a ref so the parent
 * can pull the PNG only when it actually saves. Strokes are kept in state (not
 * just on the canvas) so a container resize or a device-pixel-ratio change can
 * repaint without losing the signature.
 */
const SignaturePad = forwardRef(
  (
    {
      value,
      disabled = false,
      height = 132,
      placeholder = "Sign here",
      penColor = "#1e293b",
      onChange,
      onClear,
      className = "",
    },
    ref,
  ) => {
    const canvasRef = useRef(null);
    const wrapRef = useRef(null);
    const strokesRef = useRef([]);
    const activeStrokeRef = useRef(null);
    const [hasInk, setHasInk] = useState(false);
    const [imageFailed, setImageFailed] = useState(false);

    const repaint = useCallback(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      const ratio = window.devicePixelRatio || 1;

      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.restore();

      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = penColor;

      strokesRef.current.forEach((stroke) => {
        if (stroke.length < 2) {
          if (stroke.length === 1) {
            ctx.beginPath();
            ctx.arc(stroke[0].x, stroke[0].y, 1, 0, Math.PI * 2);
            ctx.fillStyle = penColor;
            ctx.fill();
          }
          return;
        }
        ctx.beginPath();
        ctx.moveTo(stroke[0].x, stroke[0].y);
        for (let i = 1; i < stroke.length; i += 1) {
          ctx.lineTo(stroke[i].x, stroke[i].y);
        }
        ctx.stroke();
      });
    }, [penColor]);

    const resize = useCallback(() => {
      const canvas = canvasRef.current;
      const wrap = wrapRef.current;
      if (!canvas || !wrap) return;
      const ratio = window.devicePixelRatio || 1;
      const width = wrap.clientWidth;
      if (!width) return;
      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(height * ratio);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      repaint();
    }, [height, repaint]);

    // A stored signature renders as an image; anything else shows the canvas.
    const showImage = Boolean(value) && !imageFailed;

    useEffect(() => {
      setImageFailed(false);
    }, [value]);

    // `showImage` is a dependency so the canvas is measured again whenever it
    // comes back into the tree (e.g. after "Re-sign").
    useLayoutEffect(() => {
      if (!showImage) resize();
    }, [resize, showImage]);

    useEffect(() => {
      if (showImage) return undefined;
      const wrap = wrapRef.current;
      if (!wrap || typeof ResizeObserver === "undefined") {
        window.addEventListener("resize", resize);
        return () => window.removeEventListener("resize", resize);
      }
      const observer = new ResizeObserver(resize);
      observer.observe(wrap);
      return () => observer.disconnect();
    }, [resize, showImage]);

    const pointFromEvent = (event) => {
      const rect = canvasRef.current.getBoundingClientRect();
      return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
    };

    const handlePointerDown = (event) => {
      if (disabled) return;
      event.preventDefault();
      canvasRef.current.setPointerCapture?.(event.pointerId);
      activeStrokeRef.current = [pointFromEvent(event)];
      strokesRef.current = [...strokesRef.current, activeStrokeRef.current];
      repaint();
    };

    const handlePointerMove = (event) => {
      if (disabled || !activeStrokeRef.current) return;
      event.preventDefault();
      activeStrokeRef.current.push(pointFromEvent(event));
      repaint();
    };

    const endStroke = (event) => {
      if (!activeStrokeRef.current) return;
      canvasRef.current?.releasePointerCapture?.(event.pointerId);
      activeStrokeRef.current = null;
      setHasInk(true);
      onChange?.(canvasRef.current.toDataURL("image/png"));
    };

    const reset = useCallback(() => {
      strokesRef.current = [];
      activeStrokeRef.current = null;
      setHasInk(false);
      repaint();
    }, [repaint]);

    // Only the user-facing button reports a clear; a programmatic reset (e.g.
    // after a successful save) must not look like the signer erased anything.
    const handleClearClick = useCallback(() => {
      reset();
      onClear?.();
    }, [onClear, reset]);

    useImperativeHandle(
      ref,
      () => ({
        isEmpty: () => strokesRef.current.length === 0,
        reset,
        toDataURL: () =>
          strokesRef.current.length === 0
            ? null
            : canvasRef.current?.toDataURL("image/png") || null,
      }),
      [reset],
    );

    if (showImage) {
      return (
        <div className={`relative ${className}`}>
          <div
            className="flex items-center justify-center rounded-lg border border-slate-200 bg-slate-50/60 overflow-hidden"
            style={{ height }}
          >
            <img
              src={value}
              alt="Signature"
              className="max-h-full max-w-full object-contain"
              onError={() => setImageFailed(true)}
            />
          </div>
          {!disabled && (
            <button
              type="button"
              onClick={handleClearClick}
              className="absolute top-1.5 right-1.5 px-2 py-0.5 text-[10px] font-semibold rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900"
            >
              Re-sign
            </button>
          )}
        </div>
      );
    }

    return (
      <div className={`relative ${className}`} ref={wrapRef}>
        <canvas
          ref={canvasRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endStroke}
          onPointerLeave={endStroke}
          onPointerCancel={endStroke}
          className={`block w-full rounded-lg border border-dashed bg-white ${
            disabled
              ? "border-slate-200 cursor-not-allowed opacity-60"
              : "border-slate-300 cursor-crosshair touch-none hover:border-indigo-300"
          }`}
          style={{ height }}
        />
        {!hasInk && (
          <span className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-1 px-3 text-center text-xs italic text-slate-400">
            {imageFailed ? (
              <>
                <span className="not-italic font-semibold text-amber-600">
                  Saved signature could not be loaded
                </span>
                <span>{disabled ? "Not signed" : placeholder}</span>
              </>
            ) : (
              (disabled && "Not signed") || placeholder
            )}
          </span>
        )}
        {hasInk && !disabled && (
          <button
            type="button"
            onClick={handleClearClick}
            className="absolute top-1.5 right-1.5 px-2 py-0.5 text-[10px] font-semibold rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900"
          >
            Clear
          </button>
        )}
      </div>
    );
  },
);

SignaturePad.displayName = "SignaturePad";

SignaturePad.propTypes = {
  value: PropTypes.string,
  disabled: PropTypes.bool,
  height: PropTypes.number,
  placeholder: PropTypes.string,
  penColor: PropTypes.string,
  onChange: PropTypes.func,
  onClear: PropTypes.func,
  className: PropTypes.string,
};

export default SignaturePad;
